import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLayerGroup, faPlus, faEdit, faTrash, faSearch,
    faSave, faTimes, faSync, faToggleOn, faToggleOff, faSort
} from '@fortawesome/free-solid-svg-icons';
import { Modal, Spinner } from 'react-bootstrap';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';
const EMPTY_GROUP = { GroupLabel: '', GroupIcon: '', GroupOrder: 0, GroupActive: 1, ParentGroupId: null };

/**
 * GroupABM — CRUD for tf_menu_group table.
 * Groups are the sidebar section headers that contain menu items.
 */
const GroupABM = () => {
    const [groups,   setGroups]   = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [search,   setSearch]   = useState('');
    const [modal,    setModal]    = useState(false);
    const [saving,   setSaving]   = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [form,     setForm]     = useState(EMPTY_GROUP);
    const [editId,   setEditId]   = useState(null);

    useEffect(() => { fetchGroups(); }, []);

    const fetchGroups = () => {
        setLoading(true);
        request.Get(API_BASE + 'superadmin/groups', {}, r => {
            if (r.success) setGroups(r.data || []);
            else Notify({ type: 'fail', text: 'Error al cargar grupos' });
            setLoading(false);
        }, 'json', () => setLoading(false));
    };

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return groups.filter(g => !q || g.GroupLabel?.toLowerCase().includes(q));
    }, [groups, search]);

    const openNew  = () => { setForm({ ...EMPTY_GROUP }); setEditId(null); setModal(true); };
    const openEdit = (g) => {
        setForm({
            GroupLabel: g.GroupLabel,
            GroupIcon: g.GroupIcon || '',
            GroupOrder: g.GroupOrder,
            GroupActive: g.GroupActive,
            ParentGroupId: g.ParentGroupId || null
        });
        setEditId(g.GroupId);
        setModal(true);
    };
    const closeModal = () => { if (!saving) { setModal(false); setForm(EMPTY_GROUP); setEditId(null); } };

    const handleSave = () => {
        if (!form.GroupLabel) { Notify({ type: 'fail', text: 'La etiqueta es obligatoria' }); return; }
        setSaving(true);
        const done = (r) => {
            if (r.success) {
                Notify({ type: 'success', text: editId ? 'Grupo actualizado' : 'Grupo creado' });
                fetchGroups();
                closeModal();
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al guardar' });
            }
            setSaving(false);
        };
        const fail = () => setSaving(false);
        if (editId) {
            request.Put(API_BASE + `superadmin/groups/${editId}`, form, done, 'json', fail);
        } else {
            request.Post(API_BASE + 'superadmin/groups', form, done, 'json', fail);
        }
    };

    const handleDelete = (g) => {
        setDeleting(g.GroupId);
        request.Delete(API_BASE + `superadmin/groups/${g.GroupId}`, {}, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Grupo eliminado' });
                setGroups(prev => prev.filter(x => x.GroupId !== g.GroupId));
            } else {
                Notify({ type: 'fail', text: r.msg || 'No se puede eliminar (puede tener ítems asignados)' });
            }
            setDeleting(null);
        }, 'json', () => setDeleting(null));
    };

    const toggleActive = (g) => {
        request.Put(API_BASE + `superadmin/groups/${g.GroupId}`, { ...g, GroupActive: g.GroupActive ? 0 : 1 }, r => {
            if (r.success) {
                setGroups(prev => prev.map(x => x.GroupId === g.GroupId
                    ? { ...x, GroupActive: g.GroupActive ? 0 : 1 } : x
                ));
            }
        });
    };

    const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

    // For the parent selector, exclude current group and its descendants to avoid cycles
    const parentOptions = groups.filter(g => g.GroupId !== editId);

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div className="sa-search-bar" style={{ flex: 1 }}>
                    <FontAwesomeIcon icon={faSearch} style={{ color: '#475569' }} />
                    <input placeholder="Buscar grupo..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div className="d-flex gap-2">
                    <button className="sa-btn sa-btn-ghost" onClick={fetchGroups} disabled={loading}>
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                    </button>
                    <button className="sa-btn sa-btn-primary" onClick={openNew}>
                        <FontAwesomeIcon icon={faPlus} /> Nuevo Grupo
                    </button>
                </div>
            </div>

            <div className="sa-panel">
                {loading ? (
                    <div className="text-center py-5"><Spinner animation="grow" style={{ color: 'var(--sa-primary)' }} /></div>
                ) : (
                    <div className="table-responsive">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th><FontAwesomeIcon icon={faSort} className="me-1" />Ord</th>
                                    <th>Etiqueta del Grupo</th>
                                    <th>Padre</th>
                                    <th>Ícono</th>
                                    <th>Ítems</th>
                                    <th>Activo</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr><td colSpan={7} className="text-center py-4 text-muted">Sin grupos registrados</td></tr>
                                )}
                                {filtered.map(g => {
                                    const parent = groups.find(p => p.GroupId === g.ParentGroupId);
                                    return (
                                        <tr key={g.GroupId}>
                                            <td style={{ color: '#475569', width: 50 }}>{g.GroupOrder}</td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    {g.GroupIcon && (
                                                        <div style={{
                                                            width: 32, height: 32, borderRadius: 8,
                                                            background: 'rgba(168,85,247,0.1)',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            color: 'var(--sa-primary)', fontSize: '0.8rem'
                                                        }}>
                                                            ◈
                                                        </div>
                                                    )}
                                                    <span style={{ fontWeight: 600 }}>{g.GroupLabel}</span>
                                                </div>
                                            </td>
                                            <td>
                                                {parent ? (
                                                    <span className="sa-badge sa-badge-trial">{parent.GroupLabel}</span>
                                                ) : (
                                                    <em style={{ color: '#475569', fontSize: '0.75rem' }}>Raíz</em>
                                                )}
                                            </td>
                                            <td>
                                                <code style={{ color: '#64748b', fontSize: '0.78rem' }}>
                                                    {g.GroupIcon || <em style={{ opacity: 0.4 }}>Sin ícono</em>}
                                                </code>
                                            </td>
                                            <td>
                                                <span className="sa-badge sa-badge-premium">{g.ItemCount ?? 0} ítems</span>
                                            </td>
                                            <td>
                                                <button
                                                    className={`sa-btn sa-btn-sm ${g.GroupActive ? 'sa-btn-success' : 'sa-btn-ghost'}`}
                                                    onClick={() => toggleActive(g)}
                                                >
                                                    <FontAwesomeIcon icon={g.GroupActive ? faToggleOn : faToggleOff} />
                                                </button>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={() => openEdit(g)}>
                                                        <FontAwesomeIcon icon={faEdit} />
                                                    </button>
                                                    <button className="sa-btn sa-btn-danger sa-btn-sm" onClick={() => handleDelete(g)} disabled={deleting === g.GroupId}>
                                                        {deleting === g.GroupId ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faTrash} />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Modal show={modal} onHide={closeModal} centered dialogClassName="sa-modal">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title style={{ fontWeight: 800, color: '#f8fafc', fontSize: '1.1rem' }}>
                        <FontAwesomeIcon icon={faLayerGroup} style={{ color: 'var(--sa-primary)', marginRight: 8 }} />
                        {editId ? 'Editar Grupo' : 'Nuevo Grupo'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-column gap-3">
                        <div>
                            <label className="sa-form-label">Etiqueta del Grupo *</label>
                            <input className="sa-input" placeholder="ej: Gestión" value={form.GroupLabel}
                                onChange={e => f('GroupLabel', e.target.value)} />
                        </div>
                        <div>
                            <label className="sa-form-label">Grupo Padre (Opcional)</label>
                            <select className="sa-input" value={form.ParentGroupId || ''} onChange={e => f('ParentGroupId', parseInt(e.target.value) || null)}>
                                <option value="">-- Sin Padre (Raíz) --</option>
                                {parentOptions.map(p => (
                                    <option key={p.GroupId} value={p.GroupId}>{p.GroupLabel}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="sa-form-label">Ícono (FontAwesome key)</label>
                            <input className="sa-input" placeholder="ej: faBuilding" value={form.GroupIcon}
                                onChange={e => f('GroupIcon', e.target.value)} />
                        </div>
                        <div className="row g-3">
                            <div className="col-6">
                                <label className="sa-form-label">Orden</label>
                                <input className="sa-input" type="number" value={form.GroupOrder}
                                    onChange={e => f('GroupOrder', parseInt(e.target.value) || 0)} />
                            </div>
                            <div className="col-6">
                                <label className="sa-form-label">Estado</label>
                                <select className="sa-input" value={form.GroupActive} onChange={e => f('GroupActive', parseInt(e.target.value))}>
                                    <option value={1}>Activo</option>
                                    <option value={0}>Inactivo</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <button className="sa-btn sa-btn-ghost" onClick={closeModal}><FontAwesomeIcon icon={faTimes} /> Cancelar</button>
                    <button className="sa-btn sa-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : <><FontAwesomeIcon icon={faSave} /> Guardar</>}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default GroupABM;
